<template>
  <div>
    <!-- Body -->
    <div class="body">
      <div>
        <themed-table :headers="this.bedManagerService.selectedJob.headers" :data="transformedData"
          :version="version"
          :sortingEnabled="true" :localSorting="true" :filteringEnabled="false" :paginationEnabled="false"
          :syncSearchValues="deviceSearchValues"
          @searchChange="(event) => { deviceSearchValues = event; }" @sortingChange="
            (event) => {
              localSorting = event;
            }
          " @actionClick="onActionClick" @rowClick="onRowClick">
          <template #toolbar>
            <label v-if="hasQueuedDevices" class="bulk-cancel-label">
              Select Queued/Scheduled Jobs
              <span class="bulk-cancel-checkbox-wrap">
                <input
                  type="checkbox"
                  :checked="bulkMode"
                  @change="onBulkModeToggle($event.target.checked)"
                  class="bulk-cancel-checkbox"
                />
                <span class="bulk-cancel-checkbox-mark"></span>
              </span>
            </label>
              <span
                 v-if="hasQueuedDevices"
                 v-b-tooltip.hover.top="bulkSelectedIds.length ? 'Cancel all queued/scheduled jobs' : 'Select devices first'"
                 class="cancel-job-btn-wrapper"
                 tabindex="0">
                <button
                   :disabled="!bulkSelectedIds.length"
                   class="btn btn-sm cancel-job-btn"
                   @click="onBulkSubmit">
                   Submit
                </button>
              </span>
          </template>
        </themed-table>
      </div>
      <span v-if="this.bedManagerService.selectedJob.taskStatusMetrics.some(t => t.taskType === 'OTA')"
        v-b-tooltip.hover.left="'View Task Breakdown'" class="floating-chart-icon" @click="displayPiChart()">
        <font-awesome-icon icon="chart-pie" style="pointer-events: all" :color="'rgba(0, 181, 218, 0.9)'" />
      </span>
    </div>
  </div>
</template>
<script>
import Vue from "vue";
import VueToast from "vue-toast-notification";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import ThemedTable from "../../../shared/careai/components/ThemedTable.vue";
import BedManagerService from "../BedManager.service";
import { OTA_BED_STATUS_TOOLTIP, ENABLE_BED_REQUEST_TOOLTIP } from "../bed-manager-constants";
import { OTA_BED_ERROR } from "../bed-manager-constants";
import { mapOtaBedStatus } from "../bed-manager-constants";

const moment = require("moment");

const TaskType = {
  OTA: 'OTA',
  Configuration: 'Configuration',
  EnableBedRequest: 'EnableBedRequest',
}

const CANCELLABLE_STATUSES = ['Queued', 'Scheduled'];
const BULK_CANCEL_CALLS_PER_MINUTE = 45;
const BULK_CANCEL_BATCH_SIZE = 3;
const BULK_CANCEL_BATCH_DELAY_MS = (60000 / BULK_CANCEL_CALLS_PER_MINUTE) * BULK_CANCEL_BATCH_SIZE;

Vue.use(VueToast);
Vue.use(Loading);
Vue.component("themed-table", ThemedTable);

export default {
  name: "Job",
  components: {},
  data() {
    return {
      console: console,
      moment: moment,
      // Singleton Pattern Service
      // Contains State and API Requests
      bedManagerService: BedManagerService,
      localSorting: {},
      jobInterval: null,
      bulkMode: false,
      bulkSelectedIds: [],
      deviceSearchValues: {},
    };
  },
  computed: {
    version() {
      return this.bedManagerService ? this.bedManagerService.appVersion : '0.0.0';
    },
    hasQueuedDevices() {
      return this.bedManagerService.selectedJob.data.some(row => this.isCancellable(row));
    },
    transformedData() {
      const search = (this.deviceSearchValues.id || '').trim().toLowerCase();
      const filtered = search
        ? this.bedManagerService.selectedJob.data.filter((row) => String(row.id || '').toLowerCase().includes(search))
        : this.bedManagerService.selectedJob.data;
      const sorted = this.sortByKey(
        filtered,
        this.localSorting.type,
        this.localSorting.direction,
      );
      return sorted.map(row => {
        if (this.bulkMode) {
          if (this.isCancellable(row)) {
            // All cancellable rows are auto-selected and locked (see onBulkModeToggle) — cannot be deselected individually.
            return {
              ...row,
              action: `<span class="bulk-select-checkbox-wrap"><input type="checkbox" class="bulk-select-checkbox" checked disabled /><span class="bulk-select-checkbox-mark"></span></span>`,
            };
          }
          return { ...row, action: '' };
        }
        return {
          ...row,
          action: this.isCancellable(row) ? '<button class="btn btn-sm cancel-job-btn">Cancel</button>' : '',
        };
      });
    },
    currentQuery() {
      if (this.bedManagerService.jobs.query) {
        return this.bedManagerService.jobs.query;
      }
      return null;
    },
  },
  watch: {
    currentQuery(newQuery, oldQuery) {
      this.bedManagerService.fetchJobs();
    },
    'bedManagerService.selectedJob.id'() {
      this.deviceSearchValues = {};
      this.fetchAndFormatJob();
    },
  },
  mounted() {
    this.fetchAndFormatJob();
    clearInterval(this.jobInterval);
    this.jobInterval = setInterval(() => {
      this.fetchAndFormatJob();
    }, this.bedManagerService.DEFAULT_POLL_INTERVAL_IN_SECS * 1000);

    // Used as ON DESSTROY
    this.$router.afterEach(() => {
      try {
        clearInterval(this.jobInterval);
      } catch (e) { this.console.log(e); }
    });
  },
  unmounted() {
    clearInterval(this.jobInterval);
  },
  methods: {
    fetchAndFormatJob() {
      const selectedJob = this.bedManagerService.selectedJob;
      const jobId = selectedJob && selectedJob.id;
      if (!jobId) return;
      // Fetch devices with isOffline from the API and enrich rows
      this.bedManagerService.fetchJobById(jobId).then((job) => {
        if (job && Array.isArray(job.devices)) {
          const fullRows = this.formatSelectedJobData(job);
          this.bedManagerService.selectedJob = { ...this.bedManagerService.selectedJob, data: fullRows, taskStatusMetrics: fullRows };
        }
      });
    },

   isString(val) {
    return typeof val === "string" || val instanceof String;
    },
    formatSelectedJobData(event) {
      let jobRows = [];
      event.devices.forEach((device) => {
        event.tasks.forEach((task, index) => {
          const taskType = task.type;
          let taskDetails = '';
          if (taskType == TaskType.Configuration) {
            taskDetails = `<div style="white-space:pre-wrap;word-break: break-all">${this.formatJSON(task.payload.document)} </div>`;
          } else if (taskType === TaskType.EnableBedRequest) {
            const doc = (task.payload && task.payload.document) ? task.payload.document : (task.payload || {});
            const enableValue =
              (doc.EnableBed !== undefined
                ? doc.EnableBed
                : (doc.set && doc.set.timers ? doc.set.timers.EnableBed : undefined));
            taskDetails = `<b>EnableBed</b>: ${enableValue === undefined ? 'N/A' : String(enableValue)}`;
          } else if (task.metadata) {
            if (task.metadata.packageId) taskDetails += `<b>ID</b>: ${task.metadata.packageId} `;
            if (task.metadata.packageLabel) taskDetails += `<b>Label</b>: ${task.metadata.packageLabel} `;
            if (task.metadata.packageLabel) taskDetails += `<b>Version</b>: ${task.metadata.packageVersion} `;
          }
          const isOffline = !!device.isOffline;
          const rawTaskStatus = Array.isArray(device.tasks) && device.tasks[index] ? device.tasks[index].status : undefined;
          const taskId = task._id || (Array.isArray(device.tasks) && device.tasks[index] && device.tasks[index].id);
          jobRows.push({
            jobId: event._id,
            batchJobId: event.batchJobId,
            tenant: event.tenant,
            id: `${device.id}`,
            taskId,
            rawTaskStatus,
            deviceStatus: `<div data-toggle="tooltip" title="Device ${isOffline ? 'offline' : 'online'}." class="${isOffline ? 'isNotReady' : 'isReady'}"></div>`,
            ipPrefix: 'HARDCODE (TODO)',
            taskIndex: index + 1,
            taskType,
            taskStatus:
              Array.isArray(device.tasks) && device.tasks[index] && device.tasks[index].status
                ? `${this.setStatus(device.tasks[index].status)}${device.tasks[index].errors && device.tasks[index].status.toLowerCase() !== 'error' ? ' With Errors' : ''}`
                : 'Unavailable',
            taskMessage: (() => {
              const errors = Array.isArray(device.tasks) && device.tasks[index] && device.tasks[index].errors;
              if (Array.isArray(errors)) {
                return errors.map(x => this.setErrorMessage(x)).join(', ');
              } else if (this.isString(errors)) {
                return errors.split(',').map(x => this.setErrorMessage(x)).join(', ');
              }
              else if (Array.isArray(device.tasks) && device.tasks[index].status) {
                return this.setStatusMessage(device.tasks[index].status, taskType);
              }
              return 'Unavailable';
            })(),
       
            infoIconColor: (device.tasks[index].jobStatus === 'failed' || device.tasks[index].errors) ? 'rgba(223, 74, 74, 0.9)' : 'rgba(0, 181, 218, 0.9)',
            mac: device.mac,
            taskDetails,
            action: this.isCancellable({ rawTaskStatus, taskType }) ? '<button class="btn btn-sm cancel-job-btn">Cancel</button>' : '',
            lastUpdatedAt: moment(
              (Array.isArray(device.tasks) && device.tasks[index] && device.tasks[index].lastUpdatedAt ?
                new Date(device.tasks[index].lastUpdatedAt) : new Date(event.created))
            ).format("MMMM Do, YYYY [at] h:mm A")
          })
        })
      });
      return jobRows;
    },
    formatJSON(data, indent = 1) {
      const jsonString = JSON.stringify(data, null, indent);
      return jsonString
        .replace(/[{}]/g, '')
        .replace(/^(\s*)"([^"]+)":/gm, (_, spaces, key) => {
          key = key[0].toUpperCase() + key.slice(1)
          return `${spaces}<strong>${key}</strong>:`})
        .replace(/"([^"]+)"/g, '$1')
        .replace(/,+(\n|$)/g, '$1')
        .replace(/^\s*[\r\n]/gm, '')
        .replace(/^\s{1}(\S)/gm, '$1')
        .trimEnd();
    },
    setStatusMessage(status, taskType) {
      if(taskType === TaskType.EnableBedRequest) {
        return ENABLE_BED_REQUEST_TOOLTIP[status];
      }
      const trimmedStatus = status.trim();
      if (trimmedStatus in OTA_BED_STATUS_TOOLTIP) {
        return OTA_BED_STATUS_TOOLTIP[status];
      }
      return trimmedStatus;
    },
    setStatus(status) {
      return mapOtaBedStatus(status);
    },
    setErrorMessage(error) {
      if (error in OTA_BED_ERROR) {
        return OTA_BED_ERROR[error];
      }
      return error;
    },
    sortByKey(arr, key, order = "asc") {
      return [...arr].sort((a, b) => {
        let x = a[key];
        let y = b[key];

        // Normalize undefined/null
        if (x == null) x = "";
        if (y == null) y = "";

        // Compare numbers if both look numeric
        const nx = parseFloat(x);
        const ny = parseFloat(y);
        const bothNumeric = !isNaN(nx) && !isNaN(ny);

        let cmp = 0;
        if (bothNumeric) {
          cmp = nx - ny;
        } else {
          cmp = String(x).localeCompare(String(y), undefined, {
            numeric: true,
            sensitivity: "base",
          });
        }

        return order.toLowerCase() === "desc" ? -cmp : cmp;
      });
    },
    displayPiChart() {
      this.bedManagerService.selectedJob = { ...this.bedManagerService.selectedJob, displayMetrice: true };
      setTimeout(() => this.bedManagerService.tabIndex = 0, 15);
      setTimeout(() => this.bedManagerService.tabIndex = 4, 25);
    },
    onRowClick(item, event) {
      // Selection is locked while in bulk mode (all cancellable rows are always selected) — no per-row toggle.
      if (this.bulkMode) {
        return;
      }
      if (event && event.target && event.target.closest && event.target.closest('.cancel-job-btn')) {
        this.onActionClick(item);
      }
    },
    onActionClick(item) {
      if (!item || !this.isCancellable(item) || !item.taskId) return;
      if (!window.confirm(`Cancel Job on device ${item.id}?`)) return;
      window.showLoading('Cancelling task...');
      this.bedManagerService.cancelJobDeviceTask({
        jobId: item.jobId,
        batchJobId: item.batchJobId,
        deviceId: item.id,
        taskId: item.taskId,
      }).then((res) => {
        if (!res) throw new Error('Cancel request failed.');
        Vue.$toast.open({ message: 'Task cancelled.', type: 'success' });
        this.fetchAndFormatJob();
      }).catch(() => {
        Vue.$toast.open({ message: 'Failed to cancel task due to an error.', type: 'error' });
      }).finally(() => {
        window.hideLoading();
      });
    },
    isCancellable(row) {
      return row.taskType === TaskType.OTA && CANCELLABLE_STATUSES.includes(row.rawTaskStatus);
    },
    rowKey(row) {
      return `${row.id}::${row.taskId}`;
    },
    onBulkModeToggle(checked) {
      this.bulkMode = checked;
      if (checked) {
        this.bulkSelectedIds = this.bedManagerService.selectedJob.data
          .filter(row => this.isCancellable(row) && row.taskId)
          .map(row => this.rowKey(row));
      } else {
        this.bulkSelectedIds = [];
      }
    },
    async onBulkSubmit() {
      if (!this.bulkMode || !this.bulkSelectedIds.length) return;
      if (!window.confirm(`Cancel job for ${this.bulkSelectedIds.length} device(s)?`)) return;
      window.showLoading('Cancelling tasks...');
      const selectedItems = this.bedManagerService.selectedJob.data.filter(row =>
        this.isCancellable(row) && row.taskId && this.bulkSelectedIds.includes(this.rowKey(row))
      );

      // Notify downstream consumers that the whole batch request is being cancelled before
      // touching any individual device — must succeed, otherwise abort without cancelling devices.
      try {
        const requestId = selectedItems.length ? selectedItems[0].batchJobId : null;
        const res = await this.bedManagerService.cancelJobRequest({ requestId });
        if (!res) throw new Error('Cancellation request failed.');
      } catch (e) {
        Vue.$toast.open({ message: 'Failed to publish cancellation request. No tasks were cancelled.', type: 'error' });
        window.hideLoading();
        return;
      }

      const batches = this.chunkArray(selectedItems, BULK_CANCEL_BATCH_SIZE);
      let successCount = 0;
      let failCount = 0;
      for (const batch of batches) {
        await this.sleep(BULK_CANCEL_BATCH_DELAY_MS);
        const results = await Promise.allSettled(
          batch.map(item =>
            this.bedManagerService.cancelJobDeviceTask({
              jobId: item.jobId,
              batchJobId: item.batchJobId,
              deviceId: item.id,
              taskId: item.taskId,
            })
          )
        );
        results.forEach(r => {
          if (r.status === 'fulfilled' && r.value) successCount++;
          else failCount++;
        });
      }
      if (failCount === 0) {
        Vue.$toast.open({ message: `${successCount} task(s) cancelled.`, type: 'success' });
      } else {
        Vue.$toast.open({ message: `${successCount} cancelled, ${failCount} failed.`, type: 'warning' });
      }
      this.bulkMode = false;
      this.bulkSelectedIds = [];
      window.hideLoading();
      this.fetchAndFormatJob();
    },
    chunkArray(arr, size) {
      const chunks = [];
      for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
      }
      return chunks;
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
  },
};
</script>

<!-- Header Styles -->
<style lang="scss" scoped>
.page-header {
  &__flex {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__statistics {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__statistics-item {
    font-size: 0.7em;
    display: flex;
    flex-direction: column;
  }
}

.body {
  &__inner {
    padding: 15px;
  }

  .floating-chart-icon {
    position: fixed;
    bottom: 40px;
    right: 20px;
    z-index: 1050;
    cursor: pointer;
    background: #fff;
    padding: 10px;
    border-radius: 50%;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 45px;
    height: 45px;
    transition: transform 0.2s ease;
  }

  .floating-chart-icon:hover {
    transform: scale(1.1);
  }


  .floating-chart-icon svg {
    width: 30px !important;
    height: 30px !important;
  }
}

.bulk-cancel-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  cursor: pointer;
  font-size: 0.85em;
  color: #555;
  user-select: none;
}

.bulk-cancel-checkbox-wrap {
  position: relative;
  display: inline-flex;
  width: 15px;
  height: 15px;
}

.bulk-cancel-checkbox {
  position: absolute;
  inset: 0;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
}

.bulk-cancel-checkbox-mark {
  position: absolute;
  inset: 0;
  border: 1.5px solid #adb5bd;
  border-radius: 3px;
  background-color: #fff;
  pointer-events: none;
}

.bulk-cancel-checkbox:checked + .bulk-cancel-checkbox-mark {
  background-color: #00b5da;
  border-color: #00b5da;
}

.bulk-cancel-checkbox:checked + .bulk-cancel-checkbox-mark::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 0px;
  width: 4px;
  height: 8px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.bulk-cancel-submit {
  cursor: pointer;
  display: flex;
  align-items: center;

  &--disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
}

.cancel-job-btn-wrapper {
  display: inline-block;

  // Lets the tooltip directive (bound on this wrapper) keep receiving hover
  // events even though the inner <button> is disabled and stops dispatching
  // its own mouse events.
  .cancel-job-btn:disabled {
    pointer-events: none;
    cursor: not-allowed;
  }
}
</style>

<style>
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

/*
 * Text color / tick marks below are set via external stylesheet rules
 * (not inline style="..." attributes or data:image/svg+xml backgrounds)
 * because the deployed server's CSP (careai/careai-app.js) sends
 * "default-src https:" with no 'unsafe-inline' and no data:, which
 * silently drops inline styles and blocks data: URI images. The local
 * dev server sends no CSP at all, so the bug only shows up once deployed.
 */
.cancel-job-btn,
.cancel-job-btn:hover,
.cancel-job-btn:focus,
.cancel-job-btn:active,
.cancel-job-btn:disabled {
  color: #fff !important;
}

.bulk-select-checkbox-wrap {
  position: relative;
  display: inline-flex;
  width: 16px;
  height: 16px;
  vertical-align: middle;
}

.bulk-select-checkbox {
  position: absolute;
  inset: 0;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
}

.bulk-select-checkbox-mark {
  position: absolute;
  inset: 0;
  border: 1.5px solid #adb5bd;
  border-radius: 3px;
  background-color: #fff;
  pointer-events: none;
}

.bulk-select-checkbox:checked + .bulk-select-checkbox-mark {
  background-color: #00b5da;
  border-color: #00b5da;
}

.bulk-select-checkbox:checked + .bulk-select-checkbox-mark::after {
  content: "";
  position: absolute;
  left: 4.5px;
  top: 1px;
  width: 4px;
  height: 8px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
</style>
